import * as THREE from 'three';

export type PartShape = 'flange' | 'gear' | 'bracket' | 'shaft' | 'pulley' | 'plate';

// Procedural stand-in parts, used until the real STL/GLB file is set for a model.
export function buildPartGeometry(shape: PartShape): THREE.BufferGeometry[] {
  switch (shape) {
    case 'flange':
      return [flange()];
    case 'gear':
      return [gear()];
    case 'bracket':
      return bracket();
    case 'shaft':
      return [shaft()];
    case 'pulley':
      return [pulley()];
    case 'plate':
      return [plate()];
  }
}

function circlePath(x: number, y: number, r: number) {
  const p = new THREE.Path();
  p.absarc(x, y, r, 0, Math.PI * 2, true);
  return p;
}

function extrude(shape: THREE.Shape, depth: number) {
  const g = new THREE.ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: true,
    bevelThickness: 0.03,
    bevelSize: 0.03,
    bevelSegments: 2,
    curveSegments: 48,
  });
  g.translate(0, 0, -depth / 2);
  return g;
}

function flange() {
  const s = new THREE.Shape();
  s.absarc(0, 0, 1.6, 0, Math.PI * 2, false);
  s.holes.push(circlePath(0, 0, 0.55));
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    s.holes.push(circlePath(Math.cos(a) * 1.2, Math.sin(a) * 1.2, 0.13));
  }
  const g = extrude(s, 0.35);
  g.rotateX(Math.PI / 2);
  return g;
}

function gear() {
  const teeth = 22;
  const rOuter = 1.6;
  const rRoot = 1.4;
  const s = new THREE.Shape();
  for (let i = 0; i < teeth; i++) {
    const a0 = (i / teeth) * Math.PI * 2;
    const step = (Math.PI * 2) / teeth;
    const pts: [number, number][] = [
      [rRoot, a0],
      [rOuter, a0 + step * 0.18],
      [rOuter, a0 + step * 0.45],
      [rRoot, a0 + step * 0.63],
    ];
    pts.forEach(([r, a], j) => {
      const x = Math.cos(a) * r;
      const y = Math.sin(a) * r;
      if (i === 0 && j === 0) s.moveTo(x, y);
      else s.lineTo(x, y);
    });
  }
  s.closePath();
  s.holes.push(circlePath(0, 0, 0.35));
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    s.holes.push(circlePath(Math.cos(a) * 0.85, Math.sin(a) * 0.85, 0.22));
  }
  const g = extrude(s, 0.4);
  g.rotateX(Math.PI / 2);
  return g;
}

function bracket() {
  const base = new THREE.Shape();
  base.moveTo(-1.2, -0.8);
  base.lineTo(1.2, -0.8);
  base.lineTo(1.2, 0.8);
  base.lineTo(-1.2, 0.8);
  base.closePath();
  base.holes.push(circlePath(-0.6, 0, 0.16), circlePath(0.6, 0, 0.16));
  const g1 = extrude(base, 0.2);
  g1.rotateX(Math.PI / 2);
  g1.translate(0, -0.7, 0.1);

  const wall = new THREE.Shape();
  wall.moveTo(-1.2, 0);
  wall.lineTo(1.2, 0);
  wall.lineTo(1.2, 1.2);
  wall.absarc(0, 1.2, 1.2, 0, Math.PI, false);
  wall.lineTo(-1.2, 0);
  wall.holes.push(circlePath(0, 1.2, 0.45));
  const g2 = extrude(wall, 0.2);
  g2.translate(0, -0.8, -0.7);
  return [g1, g2];
}

function lathe(profile: [number, number][]) {
  const g = new THREE.LatheGeometry(
    profile.map(([r, y]) => new THREE.Vector2(r, y)),
    72,
  );
  return g;
}

function shaft() {
  const g = lathe([
    [0, -2],
    [0.35, -2],
    [0.35, -1.2],
    [0.5, -1.2],
    [0.5, -0.2],
    [0.7, -0.2],
    [0.7, 0.3],
    [0.5, 0.3],
    [0.5, 1.3],
    [0.4, 1.3],
    [0.4, 2],
    [0, 2],
  ]);
  g.rotateZ(Math.PI / 2);
  return g;
}

function pulley() {
  return lathe([
    [0.3, -0.5],
    [1.5, -0.5],
    [1.5, -0.38],
    [1.1, -0.08],
    [1.1, 0.08],
    [1.5, 0.38],
    [1.5, 0.5],
    [0.3, 0.5],
    [0.3, -0.5],
  ]);
}

function plate() {
  const w = 1.7;
  const h = 1.1;
  const r = 0.2;
  const s = new THREE.Shape();
  s.moveTo(-w + r, -h);
  s.lineTo(w - r, -h);
  s.quadraticCurveTo(w, -h, w, -h + r);
  s.lineTo(w, h - r);
  s.quadraticCurveTo(w, h, w - r, h);
  s.lineTo(-w + r, h);
  s.quadraticCurveTo(-w, h, -w, h - r);
  s.lineTo(-w, -h + r);
  s.quadraticCurveTo(-w, -h, -w + r, -h);
  [
    [-1.35, -0.75],
    [1.35, -0.75],
    [-1.35, 0.75],
    [1.35, 0.75],
  ].forEach(([x, y]) => s.holes.push(circlePath(x, y, 0.13)));
  const slot = new THREE.Path();
  slot.moveTo(-0.6, -0.15);
  slot.lineTo(0.6, -0.15);
  slot.absarc(0.6, 0, 0.15, -Math.PI / 2, Math.PI / 2, false);
  slot.lineTo(-0.6, 0.15);
  slot.absarc(-0.6, 0, 0.15, Math.PI / 2, (Math.PI * 3) / 2, false);
  s.holes.push(slot);
  const g = extrude(s, 0.18);
  g.rotateX(Math.PI / 2);
  return g;
}
